import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import CheckBox from '@app/components/CheckBox'
import DateSelector from '@app/components/DateSelector'
import DueDiligenceItemResponsibilitySelector from '@app/components/DueDiligenceItemResponsibilitySelector'
import DueDiligenceItemTypeSelector from '@app/components/DueDiligenceItemTypeSelector'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import PillListRenderer from '@app/components/PillListRenderer'
import RemindersEditor from '@app/components/RemindersEditor'
import { DueDiligenceItemType } from '@app/models/enums'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { formatDate, humanize } from '@app/utils/formatting'
import * as notifications from '@app/utils/notifications'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap'
import {
  actionCreators,
  ATTACHMENTS_IDENTIFIER,
  IDueDiligenceItemEditState,
} from '../modules/dueDiligenceItemEdit'

type DueDiligenceItemEditModalProps = IDueDiligenceItemEditState &
  typeof actionCreators &
  typeof notifications &
  ICurrentUserState

export class DueDiligenceItemEditModal extends React.PureComponent<DueDiligenceItemEditModalProps> {
  private formRef: React.RefObject<HTMLFormElement>

  constructor(props: DueDiligenceItemEditModalProps) {
    super(props)
    this.formRef = React.createRef()
    bindFunctions(this)
  }

  public render() {
    return (
      <Modal
        centered
        isOpen={this.props.isOpen}
        size='lg'
        toggle={this.props.resetState}
      >
        <form ref={this.formRef} autoComplete='off'>
          <ModalHeader toggle={this.props.resetState}>
            {this._getTitle()}
          </ModalHeader>
          <ModalBody>
            {this._isGuest()
              ? this._renderGuestGeneral()
              : this._renderUserGeneral()}
            {this.props.item.id &&
              this.props.item.type === DueDiligenceItemType.CheckOff &&
              this._renderCheckOffFields()}
            {this.props.item.id &&
              this.props.item.type === DueDiligenceItemType.Date &&
              this._renderDateFields()}
            {this.props.item.id &&
              this.props.item.type === DueDiligenceItemType.Document &&
              this._renderDocumentFields()}
            {this.props.item.id &&
              this.props.item.type === DueDiligenceItemType.EarnestMoney &&
              this._renderEarnestMoneyFields()}
          </ModalBody>
          <ModalFooter>
            <Button color='secondary' onClick={this.props.resetState} outline>
              <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
            </Button>
            <Button color='primary' onClick={this._onSave}>
              <FontAwesomeIcon icon={['fal', 'save']} fixedWidth /> Save
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    )
  }

  private _getTitle(): string {
    if (this.props.item.id) {
      return `Edit ${this.props.item.title}`
    } else {
      return `Add ${this.props.item.pipelineType} Pipeline Item`
    }
  }

  private _isGuest(): boolean {
    return this.props.user === undefined || this.props.user.permissions.isGuest
  }

  private _isValid(): boolean {
    return this.formRef.current !== null && this.formRef.current.checkValidity()
  }

  private _onSave(): void {
    if (this._isValid()) {
      this.props.saveDueDiligenceItem(this.props.item)
    } else if (this.formRef.current) {
      const formLength = this.formRef.current.length

      const invalidFields = []

      for (let i = 0; i < formLength; i++) {
        const elem = this.formRef.current[i] as HTMLInputElement

        if (elem.nodeName.toLowerCase() !== 'button') {
          if (elem.validity && !elem.validity.valid) {
            invalidFields.push(humanize(elem.name))
          }
        }
      }

      this.props.errorToast(
        'Validation Errors',
        `The following fields have validation errors:\n\n${invalidFields.join(
          '\n',
        )}`,
      )
    }
  }

  private _renderCheckOffFields() {
    return (
      <div>
        <FormGroupRenderer>
          <CheckBox
            onChange={this.props.setIsComplete}
            value={this.props.item.isComplete}
          >
            Completed
          </CheckBox>
        </FormGroupRenderer>
      </div>
    )
  }

  private _renderDateFields() {
    return (
      <div>
        <FormGroupRenderer labelText='Date Completed'>
          <DateSelector
            onChange={this.props.setCompletedDate}
            value={this.props.item.completedDate}
          />
        </FormGroupRenderer>
      </div>
    )
  }

  private _renderDocumentFields() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <div>
        <Label>Attachments</Label>
        <FileDropzone
          activeStyle={dropzoneActiveStyle}
          style={dropzoneStyle}
          identifier={ATTACHMENTS_IDENTIFIER}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select files
          </p>
        </FileDropzone>
        <FileProgress
          dataSource={this.props.item.attachments}
          identifier={ATTACHMENTS_IDENTIFIER}
        />
      </div>
    )
  }

  private _renderEarnestMoneyFields() {
    if (!this.props.item.earnestMoney) {
      return null
    }

    return (
      <div>
        <FormGroupRenderer labelText='Deposit Date'>
          <DateSelector
            onChange={this.props.setEarnestMoneyDepositDate}
            value={this.props.item.earnestMoney.depositDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='Amount'
          onChange={this.props.setEarnestMoneyAmount}
          step='0.01'
          type='number'
          value={this.props.item.earnestMoney.amount}
        />
        <FormGroupRenderer
          labelText='Holding Location'
          onChange={this.props.setEarnestMoneyHoldingLocation}
          type='textarea'
          value={this.props.item.earnestMoney.holdingLocation}
        />
        <FormGroupRenderer labelText='Withdrawl Date'>
          <DateSelector
            onChange={this.props.setEarnestMoneyWithdrawlDate}
            value={this.props.item.earnestMoney.withdrawlDate}
          />
        </FormGroupRenderer>
      </div>
    )
  }

  private _renderGuestGeneral() {
    return (
      <div>
        <FormGroupRenderer labelText='Title'>
          {this.props.item.title}
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Due Date'>
          {formatDate(this.props.item.dueDate, 'MM/DD/YYYY', 'None')}
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Assigned To'>
          <PillListRenderer
            list={this.props.item.assignedTo}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Contacts'>
          <PillListRenderer
            list={this.props.item.contacts}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </FormGroupRenderer>
      </div>
    )
  }

  private _renderUserGeneral() {
    return (
      <div>
        <FormGroupRenderer
          labelText='Title'
          name='Title'
          onChange={this.props.setTitle}
          required
          value={this.props.item.title}
        />
        {!this.props.item.id && (
          <FormGroupRenderer labelText='Completion Type'>
            <DueDiligenceItemTypeSelector
              onChange={this.props.setType}
              value={this.props.item.type}
            />
          </FormGroupRenderer>
        )}
        <FormGroupRenderer>
          <CheckBox
            onChange={this.props.setIsPublic}
            value={this.props.item.isPublic}
          >
            Make public
          </CheckBox>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Due Date'>
          <DateSelector
            onChange={this.props.setDueDate}
            value={this.props.item.dueDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer>
          <CheckBox
            onChange={this.props.setIsCriticalDate}
            value={this.props.item.isCriticalDate}
          >
            Include in critical dates
          </CheckBox>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Responsibility'>
          <DueDiligenceItemResponsibilitySelector
            onChange={this.props.setResponsibility}
            value={this.props.item.responsibility || ''}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Assigned To'>
          <AssociatedContactsSelector
            onChange={this.props.setAssignedTo}
            value={this.props.item.assignedTo}
          />
        </FormGroupRenderer>
        {this.props.item.dueDate !== undefined && (
          <FormGroupRenderer labelText='Reminders'>
            <RemindersEditor
              onChange={this.props.setReminders}
              value={this.props.item.reminders}
            />
          </FormGroupRenderer>
        )}
        <FormGroupRenderer labelText='Contacts'>
          <AssociatedContactsSelector
            onChange={this.props.setContacts}
            value={this.props.item.contacts}
          />
        </FormGroupRenderer>
      </div>
    )
  }
}
